import { Component, OnDestroy, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { combineLatest, first } from 'rxjs';
import { AppShieldWafTriggeredRulesSlideoutComponent, ShieldApiStore, TriggeredRuleItem } from '@bn/app-shield';
import { AppRoutes } from '@bn/dashboard-core/enums';
import { AbstractLazyDirective } from '@bn/shared/entities/abstracts/abstract-lazy.directive';
import { StringTMap } from '@bn/shared/entities/index';
import { StoreRequest } from '@bn/shared/state-store';
import { CardComponent } from '@bn/web-ui/card/card.component';
import { ChartThemeVars } from '@bn/web-ui/chart';
import { ChartSingleMetricComponent } from '@bn/web-ui/chart/components/charts/chart-single-metric/chart-single-metric.component';
import { ConfirmationDialogComponent } from '@bn/web-ui/dialog';
import { NotAvailableComponent } from '@bn/web-ui/not-available/not-available-component/not-available.component';
import { SideContentComponent } from '@bn/web-ui/side-content/side-content.component';
import { ShieldOverviewChartData } from '../../../../stores/helpers/shield-api-store.helper';

@Component({
  templateUrl: './app-shield-waf-triggered-rules.page.html',
  imports: [
    AppShieldWafTriggeredRulesSlideoutComponent,
    CardComponent,
    NotAvailableComponent,
    SideContentComponent,
    ChartSingleMetricComponent,
    FaIconComponent,
    NgxSkeletonLoaderComponent,
  ],
})
export class AppShieldWafTriggeredRulesPage extends AbstractLazyDirective implements OnDestroy {
  private readonly dialog = inject(BsModalService);

  readonly #shieldApiStore = inject(ShieldApiStore);

  // Store
  public triggeredRules = this.#shieldApiStore.wafTriggeredRules;
  public chartMetrics = this.#shieldApiStore.chartMetrics;
  public isRuleUpdated = this.#shieldApiStore.isFinished(StoreRequest.Update, ['TriggeredRules']);
  public getSelectedShieldId = this.#shieldApiStore.selectedEntityId();

  // Classic
  public showSideContent = false;
  public triggeredRulesInternal: TriggeredRuleItem[] = [];
  public selectedRuleId: TriggeredRuleItem['ruleId'];
  public selectedRuleIndex?: number = undefined;
  public triggeredRule: TriggeredRuleItem | null = null;
  public chartData? = signal<ShieldOverviewChartData['chartSeries']>([['time', 'Total WAF triggers']]);
  public chartHasData?: boolean;
  public chartThemeVars?: ChartThemeVars;
  public chartSummaryLabels?: StringTMap<number>;

  constructor() {
    super();

    const pullZoneId = this.#shieldApiStore.pullZoneId();

    this.contentService.setTitle('WAF');
    this.contentService.setBackButton({
      routerLink: `/${AppRoutes.CDN}/${pullZoneId}/${AppRoutes.Shield}`,
    });

    this.subscribeToStoreSignals();
  }

  public ngOnDestroy(): void {
    this.contentService.setBackButton(null);
  }

  public onRowClick(ruleId: TriggeredRuleItem['ruleId']) {
    this.setSelectedRule(ruleId);
  }

  public onSideContentClose(): void {
    this.setSelectedRule(undefined);
  }

  public onDirectionClick(direction: 'prev' | 'next'): void {
    let currentSelectedRuleIndex = this.triggeredRulesInternal.findIndex((obj) => obj.ruleId === this.selectedRuleId);
    currentSelectedRuleIndex = direction === 'next' ? currentSelectedRuleIndex + 1 : currentSelectedRuleIndex - 1;

    let updatedSelectedRuleIndex = currentSelectedRuleIndex;

    if (updatedSelectedRuleIndex < 0) updatedSelectedRuleIndex = this.triggeredRulesInternal.length - 1;
    if (updatedSelectedRuleIndex > this.triggeredRulesInternal.length - 1) updatedSelectedRuleIndex = 0;

    this.setSelectedRule(this.triggeredRulesInternal[updatedSelectedRuleIndex].ruleId);
  }

  private subscribeToStoreSignals(): void {
    this.subscriptions.add(
      combineLatest({
        triggeredRules: toObservable(this.triggeredRules),
        routeQueryParams: this.route.queryParams.pipe(first()),
      }).subscribe(({ triggeredRules, routeQueryParams }) => {
        if (triggeredRules !== null) {
          this.triggeredRulesInternal = triggeredRules;

          if (routeQueryParams.ruleId) {
            this.setSelectedRule(routeQueryParams.ruleId);
          }
        }
      }),
    );

    // Note: when rule is updated it is removed from list so we close side panel
    toObservable(this.isRuleUpdated)
      .pipe(takeUntilDestroyed())
      .subscribe((isRuleUpdated) => {
        if (!isRuleUpdated) return;

        if (this.selectedRuleId) this.setSelectedRule(undefined);
      });

    toObservable(this.chartMetrics)
      .pipe(takeUntilDestroyed())
      .subscribe((chartMetrics) => {
        if (!chartMetrics) return;

        const data = chartMetrics.wafTriggeredRulesChartData;

        this.chartData?.set(data.chartSeries);
        this.chartHasData = data.hasData;
        this.chartSummaryLabels = data.total;
      });
  }

  private setSelectedRule(ruleId: TriggeredRuleItem['ruleId'] | undefined): void {
    this.selectedRuleId = ruleId;
    this.selectedRuleIndex = this.triggeredRulesInternal.findIndex((obj) => obj.ruleId === this.selectedRuleId);

    if (ruleId && this.selectedRuleIndex === -1) {
      this.openNoDataModal();
    }

    if (ruleId && this.selectedRuleIndex > -1) {
      this.triggeredRule = this.getItem(this.triggeredRulesInternal, this.selectedRuleId);

      // Note: fetch chart data
      this.#shieldApiStore.getTriggeredRuleChartMetrics({
        id: Number(this.getSelectedShieldId()),
        ruleId,
      });

      this.showSideContent = true;
    } else {
      this.triggeredRule = null;
      this.showSideContent = false;
    }

    this.router.navigate(['./'], {
      relativeTo: this.route,
      queryParams: {
        ruleId,
      },
    });
  }

  private getItem(rules: TriggeredRuleItem[], ruleId: TriggeredRuleItem['ruleId']): TriggeredRuleItem | null {
    return rules?.filter((rule) => rule.ruleId === ruleId).at(0) ?? null;
  }

  public openNoDataModal(): void {
    const dialog = this.dialog.show(ConfirmationDialogComponent, {
      class: 'modal-sm modal-dialog-centered',
      initialState: {
        data: {
          confirmButtonName: 'Confirm',
          description: 'Current triggered rule is no longer available or it was impossible to retrive its data. Please check actual list of available records.',
          event: {
            action: 'confirm',
          },
          title: 'Triggered rule not available',
          hideCloseButton: true,
          textCenter: true,
        },
      },
    });

    this.subscriptions.add(
      dialog.content?.getOnAction$().subscribe(() => {
        this.setSelectedRule(undefined);
        dialog.hide();
      }),
    );
  }
}
